import React from "react"; 
import { Box, Typography } from "@mui/material";

const isEmpty = (value) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

const toText = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(toText).filter(Boolean).join(", ");

  if (typeof value === "object") {
    if (value.label != null && value.label !== "") return toText(value.label);
    if (value.text != null && value.text !== "") return toText(value.text);
    if ("value" in value) return toText(value.value);

    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }

  return String(value);
};

const getDisplayValue = (field, formData) => {
  const entry = formData?.[field.fieldName];
  if (entry === null || entry === undefined) return "";

  const rawValue =
    typeof entry === "object" && !Array.isArray(entry) && "value" in entry
      ? entry.value
      : entry;

  const displayValue =
    typeof entry === "object" && !Array.isArray(entry) && !isEmpty(entry.label)
      ? entry.label
      : rawValue;

  if (isEmpty(displayValue)) return "";

  const isSensitive =
    field.fieldType === "password" ||
    /password|secret|token|credential/i.test(
      `${field.fieldName} ${field.fieldLabel || ""}`
    );

  return isSensitive ? "••••••••" : toText(displayValue);
};

export default function AzureSummary({
  selectedProvider,
  formConfig = [],
  formData = {},
  additionalTags = [],
}) {
  if (selectedProvider?.toUpperCase() !== "AZURE") return null;

  const fieldGroups = new Map();

  formConfig
    .filter(
      (field) =>
        field.tabName &&
        field.tabName.toLowerCase() !== "tag" &&
        field.tabName.toLowerCase() !== "summary" &&
        field.isDisplay !== false &&
        field.fieldType !== "collection"
    )
    .forEach((field) => {
      const value = getDisplayValue(field, formData);
      if (!value) return;

      const groupName =
        field.tabName.toLowerCase() === "general" ? "Basics" : field.tabName;

      if (!fieldGroups.has(groupName)) fieldGroups.set(groupName, []);
      fieldGroups.get(groupName).push({
        label: field.fieldLabel || field.fieldName,
        value,
      });
    });

  // Azure tags defined in the form configuration.
  const tagMap = new Map();

  formConfig
    .filter(
      (field) =>
        field.tabName?.toLowerCase() === "tag" &&
        field.isDisplay !== false &&
        field.fieldType !== "collection"
    )
    .forEach((field) => {
      const value = getDisplayValue(field, formData);
      if (value) {
        tagMap.set(field.fieldLabel || field.fieldName, value);
      }
    });

  // Azure tags added through the custom Add Tag control.
  additionalTags.forEach((tag) => {
    const key = String(tag?.key || "").trim();
    const value = String(tag?.value || "").trim();

    if (key && value) tagMap.set(key, value);
  });

  const tags = Array.from(tagMap, ([label, value]) => ({ label, value }));

  const renderRows = (rows) =>
    rows.map(({ label, value }, index) => (
      <Box
        key={`${label}-${index}`}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(120px, 1fr)", sm: "minmax(220px, 1fr) 2fr" },
          gap: { xs: 0.5, sm: 2 },
          py: 0.75,
        }}
      >
        <Typography color="text.secondary">{label}</Typography>
        <Typography sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
      </Box>
    ));

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {Array.from(fieldGroups, ([groupName, rows]) => (
        <Box key={groupName} sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            {groupName}
          </Typography>
          {renderRows(rows)}
        </Box>
      ))}

      {tags.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>Tags</Typography>
          {renderRows(tags)}
        </Box>
      )}

      {fieldGroups.size === 0 && tags.length === 0 && (
        <Typography color="text.secondary">
          No Azure values have been entered yet.
        </Typography>
      )}
    </Box>
  );
}
